import { useEffect, useState } from 'react';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import PausePolicyInfoMonthView from '@/dashboard/components/Products/policyCard/components/infoView/monthInfo';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { GridItem } from '@/dashboard/mui/styledComponent';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { ClockIcon } from '@heroicons/react/outline';
import {
  PolicyPremiumDetailInfoResponse,
  apiExtendPolicyPause,
  apiGetExtendPauseDetailInfoByEndDate,
  convertDayjsTimeToServerStoredTimeData,
  formatCommonDateStr,
} from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';

type ExtendPauseDialogConfirmViewProps = CustomDialogProps & {
  policy: PolicyModel;
  endDate: Dayjs;
  back: () => void;
};
const ExtendPauseDialogConfirmView = (
  props: ExtendPauseDialogConfirmViewProps
) => {
  const { endDate, back, close, policy } = props;
  const [loading, setLoading] = useState(false);
  const [{ fetchPolicies }] = usePolicyStore(({ actions }) => [actions]);
  const [cardSelectMonth, setCardSelectMonth] = useState('');
  const [premiumDetailInfo, setPremiumDetailInfo] =
    useState<PolicyPremiumDetailInfoResponse>({ billing_info: [] });

  const closeAction = () => {
    if (close) close();
  };

  /**
   * request
   */
  const getRequestParams = () => {
    return {
      pause_id: policy?.pauses?.[0]?.id,
      pause_end_date: convertDayjsTimeToServerStoredTimeData(endDate),
    };
  };

  const requestExtendPauseDetailInfo = async () => {
    const params = getRequestParams();

    setLoading(true);
    const [res] = await apiGetExtendPauseDetailInfoByEndDate(params);
    setLoading(false);

    if (res) {
      if (res.billing_info.length > 0) {
        setCardSelectMonth(res.billing_info[0].monthText);
      }
      setPremiumDetailInfo(res);
    }
  };
  // extend pause
  const requestExtendPause = async () => {
    const params = getRequestParams();

    setLoading(true);
    const [res, err] = await apiExtendPolicyPause(params);
    setLoading(false);

    if (res) {
      fetchPolicies();
      toast.success('Extend pause success!');
      closeAction();
    }
  };

  useEffect(() => {
    requestExtendPauseDetailInfo();
  }, []);

  /**
   * methods
   */

  // back to select new date
  const backAction = () => {
    if (back) back();
  };

  // click card
  const clickCardAction = (index) => {
    setCardSelectMonth(premiumDetailInfo.billing_info[index].monthText);
  };

  return (
    <Box className="relative mt-4 flex flex-col items-center">
      {loading && <LogoLoading />}
      <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
        <ClockIcon className="h-7 w-7" />
      </Box>
      <Typography
        className="text-black-secondary mt-4 text-center font-black"
        variant="h5"
      >
        Extend pause
      </Typography>
      <Box className="flex flex-row items-center">
        <Typography
          className="text-black-secondary text-center"
          variant="body2"
        >
          Pause period : {formatCommonDateStr(policy.pauseStartDateDayjs)} -{' '}
          {formatCommonDateStr(endDate)}
        </Typography>
        <Box className="flex flex-row items-center">
          <span className="bg-brand-primary ml-2 h-[6px] w-[6px] rounded-full border"></span>
          <Button variant="text" onClick={backAction}>
            Change dates
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2} className="mt-2">
        {premiumDetailInfo.billing_info.map((item, index) => {
          return (
            <Grid
              item
              key={item.monthText}
              xs={12 / premiumDetailInfo.billing_info.length}
            >
              <GridItem>
                <PausePolicyInfoMonthView
                  index={index}
                  info={item}
                  click={clickCardAction}
                  isActive={cardSelectMonth === item.monthText}
                />
              </GridItem>
            </Grid>
          );
        })}
      </Grid>
      <Box className="mt-6 flex w-full flex-row justify-end">
        <Button
          className="text-brand-primary text-lg font-bold"
          variant="text"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <LoadingButton
          className="ml-2 h-12 w-[160px] rounded-full text-lg font-bold"
          loading={loading}
          loadingIndicator="Loading..."
          color="primary"
          onClick={requestExtendPause}
          variant="contained"
        >
          <span>Pause</span>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ExtendPauseDialogConfirmView;
